import React, { useCallback, useState } from 'react';
import GroupSettingCard from './groupSettingCard';
import TeamsSettingCard from './teamSettingCard';
import PrioritySettingCard from './prioritieSettindCard';
import LabelsSettingCard from './labelsSettingCard';
import Modal from '../modals/modal';
import InviteTeamsModal from '../modals/inviteTeamsModal';
import DeleteModal from '../modals/deleteModal';
import Labelmodal from '../modals/labelModal';
import { captureException } from '../../services/logService';
import { UsersWorkspaceInterface } from '../../interfaces/WorkspaceInterface';
import StatusSettingCard from './statusSettingCard';
import AddStatusModal from '../modals/addStatusmodal';
import SettingCard from './settingCard';
import InviteMemberModal from '../modals/inviteMemberModal';
import { Label, RawTaskGroup } from '../../mobx/interfaces/project';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { ProjectTemplateModel } from '../../mobx/models/projectTemplate';
import CreateGroupModal from '../modals/createGroupModal';
import { TaskGroupInterface } from '../../interfaces/TaskInterface';
import { StatusListInterface } from '../../interfaces/ProjectInterface';
import { dispatchProjectError } from '../../services/projectServices';

interface Props {
  currentProject: ProjectTemplateModel;
  themeMode: { theme: string };
  onUpdateEstimate: (value: boolean) => void;
  onUpdateMultiAssignee?: (value: boolean) => void;
  onUpdateStatus: (value: boolean) => void;
  onUpdatePriority: (value: boolean) => void;
  onUpdateLabels: (value: boolean) => void;
  taskGroups: RawTaskGroup[];
  loading: boolean;
  paramsId?: string;
  isTemplateProject?: boolean;
  loadData: () => void;
  setLoading: (value: boolean) => void;
  onUpdateGroup: (value: boolean) => void;
  loadingButton: {
    group: boolean;
    status: boolean;
    priority: boolean;
    label: boolean;
    multiAssignee: boolean;
    estimation: boolean;
    milestone: boolean;
  };
}

const TemplateSettingCards: React.FC<Props> = (props) => {
  const {
    currentProject,
    themeMode,
    onUpdateEstimate,
    onUpdateMultiAssignee,
    onUpdatePriority,
    onUpdateLabels,
    taskGroups,
    loading,
    paramsId,
    isTemplateProject = false,
    loadData,
    setLoading,
    onUpdateStatus,
    onUpdateGroup,
    loadingButton
  } = props;
  //States
  const [ModalOpen, setModalOpen] = useState(false);
  const [isMainModel, setMainModel] = useState(false);
  const [isSubModel, setSubModel] = useState(false);
  const [isDeleteMemberModel, setIsDeleteMemberModel] = useState(false);
  const [isMemberId, setIsMemberId] = useState('');
  const [isDeleteLabelModel, setIsDeleteLabelModel] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [labelId, setLabelId] = useState('');
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusType, setStatusType] = useState(0);
  const [groupData, setGroupData] = useState<TaskGroupInterface | null>(null);
  const [labelData, setLabelData] = useState<Label | null>(null);
  const [statusData, setStatusData] = useState<StatusListInterface | null>(null);

  //Mobx store variables
  const { projectTemplateStore, workspaceStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { currentProjectTemplate } = projectTemplateStore;

  const onClickDeleteLabel = useCallback(
    (labelId: string) => {
      const updatedLabels = currentProjectTemplate?.labelsList?.filter((label) => label?.id !== labelId) || [];
      currentProjectTemplate?.setLabelsList(updatedLabels);
      setIsDeleteLabelModel(false);
    },
    [currentProjectTemplate]
  );

  const onCloseGroupModal = useCallback(() => {
    setGroupData(null);
    setModalOpen(false);
  }, []);

  const onClickEditGroup = useCallback((group: TaskGroupInterface) => {
    setGroupData(group);
    setModalOpen(true);
  }, []);

  const onClickEditLabel = useCallback((item: Label) => {
    setLabelData(item);
    setIsOpen(true);
  }, []);

  const onCreateNewLabel = () => {
    setIsOpen(true);
  };

  const onCloseNewLabel = () => {
    setLabelData(null);
    setIsOpen(false);
  };

  const OpenDeleteMemberModel = (id: string) => {
    setIsDeleteMemberModel(true);
    setIsMemberId(id);
  };

  const onCloseDeleteMemberModel = () => {
    setIsDeleteMemberModel(false);
  };

  const onSubModel = () => {
    setMainModel(false);
    setSubModel(true);
  };

  const onCloseSubModal = () => {
    setSubModel(false);
  };

  const openDeleteLabelModel = (id: string) => {
    setIsDeleteLabelModel(true);
    setLabelId(id);
  };

  const closeDeleteLabelModel = () => {
    setIsDeleteLabelModel(false);
  };

  const handleDragEnd = async (sourceIndex: number, destinationIndex: number) => {
    const updatedGroupNames = Array.from(taskGroups);
    const [removed] = updatedGroupNames.splice(sourceIndex, 1);
    updatedGroupNames.splice(destinationIndex, 0, removed);

    const updated = updatedGroupNames?.map((item, index) => {
      return { ...(item || {}), index: index };
    });
    currentProjectTemplate?.setTaskGroups(updated);
  };

  const closeInviteModal = () => {
    setMainModel(false);
  };

  const onClickDeleteMember = useCallback(
    (memberId: string) => {
      const updatedUsers = currentProjectTemplate?.users
        ?.filter((user) => user?.id !== memberId)
        ?.map((user) => user?.id);
      currentProjectTemplate?.setUsersIds(updatedUsers || []);
      onCloseDeleteMemberModel();
    },
    [currentProjectTemplate]
  );

  const onClickMemberBox = useCallback(
    async (userIndex: number, invitedUser: UsersWorkspaceInterface) => {
      try {
        setLoading(true);
        if (userIndex === -1) {
          const updatedUsers = [...(currentProjectTemplate?.userIds || []), invitedUser?.id];
          currentProjectTemplate?.setUsersIds(updatedUsers);
        } else {
          onClickDeleteMember(invitedUser?.id);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [setLoading, currentProjectTemplate, onClickDeleteMember]
  );

  const deleteGroup = useCallback(
    async (item: TaskGroupInterface) => {
      try {
        setLoading(true);
        const isGroupInUse = currentProjectTemplate?.tasks?.some((task) => task?.groupId === item?.id);
        if (isGroupInUse) {
          dispatchProjectError(`Unable to delete group "${item?.name}", as it is currently in use!`);
        } else {
          const updatedGroups = currentProjectTemplate?.taskGroups?.filter((group) => group?.id !== item?.id) || [];
          currentProjectTemplate?.setTaskGroups(updatedGroups);
        }
      } catch (error) {
        captureException(error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading, currentProjectTemplate]
  );

  const onCloseStatusModal = useCallback(() => {
    setStatusData(null);
    setIsStatusModalOpen(false);
  }, []);

  return (
    <>
      <TeamsSettingCard
        setMainModel={(value) => setMainModel(value)}
        OpenDeleteMemberModel={(id) => OpenDeleteMemberModel(id)}
        isTemplateProject={isTemplateProject}
      />
      <GroupSettingCard
        themeMode={themeMode}
        handleDragEnd={handleDragEnd}
        taskGroups={taskGroups}
        onClickEditGroup={(group) => onClickEditGroup(group)}
        deleteGroup={(group) => deleteGroup(group)}
        loading={loading}
        setModalOpen={(value) => setModalOpen(value)}
        onUpdateGroup={(value) => onUpdateGroup(value)}
        loadingButton={loadingButton?.group}
        isTemplateProject={isTemplateProject}
      />
      <StatusSettingCard
        themeMode={themeMode}
        loadingButton={loadingButton?.status}
        setModalOpen={(value) => setIsStatusModalOpen(value)}
        onUpdateStatus={(value) => onUpdateStatus(value)}
        setStatusType={(value) => setStatusType(value)}
        isTemplateProject={isTemplateProject}
        setStatusData={setStatusData}
      />
      <PrioritySettingCard
        themeMode={themeMode}
        onUpdatePriority={(value) => onUpdatePriority(value)}
        loadingButton={loadingButton?.priority}
        isTemplate={isTemplateProject}
      />
      <LabelsSettingCard
        onUpdateLabels={(value) => onUpdateLabels(value)}
        openDeleteLabelModel={(id) => openDeleteLabelModel(id)}
        onClickEditLabel={(item) => onClickEditLabel(item)}
        handleclick={onCreateNewLabel}
        loadingButton={loadingButton?.label}
        isTemplateProject={isTemplateProject}
      />

      {/* multi assign or single assign */}
      <SettingCard
        isTemplateProject={isTemplateProject}
        onUpdateSetting={(value) => {
          if (onUpdateMultiAssignee) onUpdateMultiAssignee(value);
        }}
        loadingButton={loadingButton?.multiAssignee}
        title='Multiple assignee'
        subtitle='To enable this feature, you can allow tasks to be assigned to multiple members.'
      />
      {/* Estimation on and off */}
      <SettingCard
        isTemplateProject={isTemplateProject}
        onUpdateSetting={(value) => {
          if (onUpdateEstimate) onUpdateEstimate(value);
        }}
        loadingButton={loadingButton?.estimation}
        title='Estimation'
        subtitle='Estimates communicate task complexity and cycle capacity. Choose how to estimate with your team below.'
      />
      <Modal open={isMainModel} onClose={closeInviteModal} maxWidth={'400px'}>
        <InviteMemberModal
          onClose={closeInviteModal}
          workspace={workspace!}
          currentProject={currentProjectTemplate!}
          onClickMemberBox={(userIndex, userItem) => onClickMemberBox(userIndex, userItem as UsersWorkspaceInterface)}
          onSubModel={onSubModel}
          isProjectTemplate={true}
        />
      </Modal>
      <Modal open={ModalOpen} onClose={onCloseGroupModal} maxWidth={'462px'}>
        <CreateGroupModal
          groupData={groupData}
          loadData={loadData}
          onClose={onCloseGroupModal}
          isTemplateProject={true}
        />
      </Modal>
      <Modal open={isStatusModalOpen} onClose={onCloseStatusModal} maxWidth={'334px'}>
        <AddStatusModal
          onCancel={onCloseStatusModal}
          statusType={statusType}
          projectId={currentProject?.id || ''}
          isTemplateProject={true}
          statusData={statusData}
        />
      </Modal>
      <Modal open={isSubModel} onClose={onCloseSubModal} maxWidth={'400px'}>
        <InviteTeamsModal
          onCancel={onCloseSubModal}
          project_id={paramsId}
          isTextVisible={true}
          projectName={currentProject?.name}
          workspace_id={workspace?.id!}
        />
      </Modal>
      <Modal open={isDeleteMemberModel} onClose={onCloseDeleteMemberModel} maxWidth={'334px'}>
        <DeleteModal
          onClose={onCloseDeleteMemberModel}
          loading={loading}
          buttonText='Remove'
          onOk={() => onClickDeleteMember(isMemberId)}
          modalTitle='Remove Member?'
          description='Are you sure you want to remove this member from the project?'
        />
      </Modal>
      <Modal open={isDeleteLabelModel} onClose={closeDeleteLabelModel} maxWidth={'334px'}>
        <DeleteModal
          onClose={closeDeleteLabelModel}
          loading={loading}
          onOk={() => onClickDeleteLabel(labelId)}
          modalTitle='Delete Label?'
          description='Are you sure you want to delete this label?'
        />
      </Modal>
      <Modal open={isOpen} onClose={onCloseNewLabel} maxWidth={'462px'}>
        <Labelmodal isTemplateProject={true} onClose={onCloseNewLabel} labelData={labelData} />
      </Modal>
    </>
  );
};

export default observer(TemplateSettingCards);
